"use client"

import type { Transition, Variants } from "motion/react"
import { motion, useAnimation } from "motion/react"
import type { HTMLAttributes } from "react"
import * as React from "react"
import { forwardRef, useCallback, useImperativeHandle } from "react"

import { cn } from "@/lib/utils"
import { useHoverResetGuard } from "./useHoverResetGuard"

export interface CpuIconHandle {
  startAnimation: () => void
  stopAnimation: () => void
}

interface CpuIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number
  /** e.g. `inherit` from sidebar nav so stroke follows the row color */
  color?: string
}

const TRANSITION: Transition = {
  duration: 0.5,
  ease: "easeInOut",
  repeat: 1,
}

const Y_VARIANTS: Variants = {
  normal: {
    scale: 1,
    rotate: 0,
    opacity: 1,
  },
  animate: {
    scaleY: [1, 1.5, 1],
    opacity: [1, 0.8, 1],
  },
}

const X_VARIANTS: Variants = {
  normal: {
    scale: 1,
    rotate: 0,
    opacity: 1,
  },
  animate: {
    scaleX: [1, 1.5, 1],
    opacity: [1, 0.8, 1],
  },
}

const PIN_PATHS = [
  { d: "M15 2v2", variants: Y_VARIANTS },
  { d: "M15 20v2", variants: Y_VARIANTS },
  { d: "M2 15h2", variants: X_VARIANTS },
  { d: "M2 9h2", variants: X_VARIANTS },
  { d: "M20 15h2", variants: X_VARIANTS },
  { d: "M20 9h2", variants: X_VARIANTS },
  { d: "M9 2v2", variants: Y_VARIANTS },
  { d: "M9 20v2", variants: Y_VARIANTS },
] as const

const CpuIcon = forwardRef<CpuIconHandle, CpuIconProps>(
  (
    { onMouseEnter, onMouseLeave, className, size = 28, color, style, ...props },
    ref
  ) => {
    const controls = useAnimation()
    const { withLockedHoverReset, releaseHoverResetLock, shouldApplyHoverReset } =
      useHoverResetGuard()

    const runCpuAnimation = useCallback(async () => {
      await controls.start("normal", { duration: 0 })
      await controls.start("animate")
    }, [controls])

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () => void withLockedHoverReset(runCpuAnimation),
        stopAnimation: () => {
          releaseHoverResetLock()
          void controls.start("normal")
        },
      }),
      [controls, releaseHoverResetLock, runCpuAnimation, withLockedHoverReset]
    )

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e)
        void runCpuAnimation()
      },
      [onMouseEnter, runCpuAnimation]
    )

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e)
        if (!shouldApplyHoverReset()) return
        void controls.start("normal")
      },
      [controls, onMouseLeave, shouldApplyHoverReset]
    )

    return (
      <div
        className={cn("inline-flex shrink-0 items-center justify-center", className)}
        style={{
          color: color && color !== "inherit" ? color : undefined,
          ...style,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect height="16" rx="2" width="16" x="4" y="4" />
          <rect height="6" rx="1" width="6" x="9" y="9" />
          {PIN_PATHS.map((pin) => (
            <motion.path
              key={pin.d}
              animate={controls}
              d={pin.d}
              initial="normal"
              transition={TRANSITION}
              variants={pin.variants}
            />
          ))}
        </svg>
      </div>
    )
  }
)

CpuIcon.displayName = "CpuIcon"

export { CpuIcon }
